/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

/**
 * "Liberar o plano completo" (RN-10): whether the offer exists and what it states.
 */

import type { SubscriptionDetail } from '@services/requests/housien-services/AgenciesService.subscription';

/** Everything the strip and the confirmation need to state, and nothing else. */
export interface TrialOffer {
  planName: string;
  /** What is charged today (the same figure as "Primeira cobrança", AR-07). */
  cents: number;
  interactions: number;
  searches: number;
  /** ISO date the trial ends and the first charge was due. */
  trialEnd: string;
  brand: string | null;
  lastFour: string | null;
  frequency: 'monthly' | 'yearly';
}

export interface TrialOfferExtras {
  planName: string;
  limits: { interactions: number; searches: number } | null;
  cents: number | null;
}

/** Only a `trialing` subscription that is not already cancelled has an offer. */
const isOpenTrial = (subscription: SubscriptionDetail): boolean => (
  subscription.subscription_status === 'trialing' && !subscription.cancelled_at
);

/** A figure the promise needs and the data did not carry. */
const isMissing = (value: unknown): boolean => value == null || value === '';

/**
 * The offer to end the trial, or null when there is nothing honest to offer.
 *
 * Only a `trialing` subscription that is not already cancelled has one, and
 * only when the amount, the quota and the date it promises are all known.
 *
 * @param subscription - The subscription as the plan tab read it.
 * @param extras - Plan name, quota per seat and the amount charged today.
 * @returns The offer, or null.
 */
export function trialOffer(
  subscription: SubscriptionDetail,
  extras: TrialOfferExtras,
): TrialOffer | null {
  const { planName, limits, cents } = extras;
  const trialEnd = subscription.next_renewal_date;
  if (!isOpenTrial(subscription) || [planName, limits, cents, trialEnd].some(isMissing)) return null;
  return {
    planName,
    cents: cents as number,
    interactions: (limits as NonNullable<typeof limits>).interactions,
    searches: (limits as NonNullable<typeof limits>).searches,
    trialEnd: trialEnd as string,
    brand: subscription.payment_method?.brand ?? null,
    lastFour: subscription.payment_method?.last_four ?? null,
    frequency: subscription.billing?.billing_frequency === 'yearly' ? 'yearly' : 'monthly',
  };
}

type SeatLimits = Record<string, number | null | undefined> | null | undefined;

/** What one seat gets a month, or null when the catalogue does not say both. */
export function seatQuota(limits: SeatLimits): TrialOfferExtras['limits'] {
  const interactions = limits?.interactions;
  const searches = limits?.partnership_searches;
  return interactions == null || searches == null ? null : { interactions, searches };
}

export interface OfferContext {
  titleOf: (planId: string) => string | null;
  /** The catalogue's per-seat limits of a plan line, by the line's plan id. */
  seatLimitsOf: (planId: string) => SeatLimits;
  /** Name of the single plan when the subscription has no lines. */
  soloName: string;
  /** Today's charge: the renewal total for a team, the billed price for a solo plan. */
  renewalCents: number;
  priceCents: number | null;
}

/**
 * Builds the offer from what the plan tab already has at hand.
 *
 * A team is named and sized by its first line; a subscription without lines
 * reads the plan block. The amount is the one "Primeira cobrança" shows.
 *
 * @param subscription - The subscription as the plan tab read it.
 * @param ctx - Catalogue lookups and the amounts.
 * @returns The offer, or null (see {@link trialOffer}).
 */
export function buildTrialOffer(
  subscription: SubscriptionDetail,
  ctx: OfferContext,
): TrialOffer | null {
  const line = subscription.items?.[0];
  return trialOffer(subscription, {
    planName: (line ? ctx.titleOf(line.plan_id) : ctx.soloName) ?? '',
    limits: seatQuota(line ? ctx.seatLimitsOf(line.plan_id) : subscription.plan?.seat_limits),
    cents: line ? ctx.renewalCents : ctx.priceCents,
  });
}

